/**
 * Pure sort / filter / search logic for the CRM scoreboard (A7Y-4981).
 *
 * The page loads the whole account list once (~100 rows) and works on it here.
 * Counts are never computed here — the API supplies `customerByStage` /
 * `partnerByStage`.
 */
import { CRM_PRIORITIES } from '@a7y/types';
import type { CrmAccountListItem, CrmPriority, CrmSegment, CrmStage } from '@a7y/types';

/** Closest-to-signed first, as `pipeline.md`'s scoreboard orders it. */
export const STAGE_ORDER: readonly CrmStage[] = [
  'onboarded',
  'signed',
  'proposal',
  'trial',
  'demo',
  'conversation',
  'lead',
  'dormant',
  'closed',
];

const SEGMENT_ORDER: readonly CrmSegment[] = ['enterprise', 'mid', 'agency', 'partner'];

/**
 * A7Y-5203: the rank's position, highest first. An account with no rank is P8
 * by the dashboard's rule, so it sorts just after an explicit P8 and before
 * `unavailable`.
 */
export function rankOrder(priority: CrmPriority | null | undefined): number {
  if (priority === null || priority === undefined) return CRM_PRIORITIES.indexOf('P8') + 0.5;
  return CRM_PRIORITIES.indexOf(priority);
}

/** The rank as the page shows it: `P1`…`P8`, `P5b`, `Unavailable`, or `—` when not ranked. */
export function rankLabel(priority: CrmPriority | null | undefined): string {
  if (priority === null || priority === undefined) return '—';
  return priority === 'unavailable' ? 'Unavailable' : priority;
}

export type SortKey =
  | 'rank'
  | 'account'
  | 'contact'
  | 'stage'
  | 'segment'
  | 'lastTouch'
  | 'nextAction'
  | 'nextDate';
export type SortDir = 'asc' | 'desc';
export interface SortState {
  key: SortKey;
  dir: SortDir;
}

/** The page's initial order (A7Y-5203): rank, highest first; ties by stage (closest to signed first), then name. */
export const DEFAULT_SORT: SortState = { key: 'rank', dir: 'asc' };

export type KindFilter = 'any' | 'customer' | 'partner';
export type RankFilter = CrmPriority | 'unranked' | 'any';
export interface Filters {
  rank: RankFilter;
  stage: CrmStage | 'any';
  segment: CrmSegment | 'any';
  kind: KindFilter;
  staleOnly: boolean;
  search: string;
}
export const NO_FILTERS: Filters = {
  rank: 'any',
  stage: 'any',
  segment: 'any',
  kind: 'any',
  staleOnly: false,
  search: '',
};

/** The text the next-action column shows: the action, else the `Watch:` condition. */
export function nextActionText(item: CrmAccountListItem): string | null {
  const { nextAction, watch } = item.account;
  if (nextAction) return nextAction;
  return watch ? `Watch: ${watch}` : null;
}

/** A sortable value; `null` always sorts last, in either direction. */
function sortValue(item: CrmAccountListItem, key: SortKey): string | number | null {
  switch (key) {
    case 'rank':
      return rankOrder(item.account.priority);
    case 'account':
      return item.account.name.toLowerCase();
    case 'contact':
      return item.primaryContact?.name.toLowerCase() ?? null;
    case 'stage':
      return STAGE_ORDER.indexOf(item.account.stage);
    case 'segment':
      return SEGMENT_ORDER.indexOf(item.account.segment);
    case 'lastTouch':
      return item.account.lastTouch;
    case 'nextAction':
      return nextActionText(item)?.toLowerCase() ?? null;
    case 'nextDate':
      return item.nextDate;
  }
}

function compareNames(a: CrmAccountListItem, b: CrmAccountListItem): number {
  return a.account.name.localeCompare(b.account.name, undefined, { sensitivity: 'base' });
}

export function sortAccounts(items: readonly CrmAccountListItem[], sort: SortState): CrmAccountListItem[] {
  const sign = sort.dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const va = sortValue(a, sort.key);
    const vb = sortValue(b, sort.key);
    if (va === null && vb !== null) return 1;
    if (vb === null && va !== null) return -1;
    if (va !== null && vb !== null && va !== vb) return (va < vb ? -1 : 1) * sign;
    if (sort.key === 'rank') {
      const sa = STAGE_ORDER.indexOf(a.account.stage);
      const sb = STAGE_ORDER.indexOf(b.account.stage);
      if (sa !== sb) return sa - sb;
    }
    return compareNames(a, b);
  });
}

/** Clicking the active column reverses it; clicking another starts it ascending. */
export function nextSort(current: SortState, key: SortKey): SortState {
  if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: 'asc' };
}

export function filterAccounts(
  items: readonly CrmAccountListItem[],
  filters: Filters
): CrmAccountListItem[] {
  const needle = filters.search.trim().toLowerCase();
  return items.filter((item) => {
    const { account } = item;
    if (filters.rank === 'unranked' && account.priority) return false;
    if (filters.rank !== 'any' && filters.rank !== 'unranked' && account.priority !== filters.rank) return false;
    if (filters.stage !== 'any' && account.stage !== filters.stage) return false;
    if (filters.segment !== 'any' && account.segment !== filters.segment) return false;
    if (filters.kind === 'partner' && account.segment !== 'partner') return false;
    if (filters.kind === 'customer' && account.segment === 'partner') return false;
    if (filters.staleOnly && !item.stale) return false;
    if (needle) {
      const names = [account.name, ...item.contacts.map((c) => c.name)];
      if (!names.some((n) => n.toLowerCase().includes(needle))) return false;
    }
    return true;
  });
}

/** A slot's or parked entry's account as the page names it. */
export interface AccountRef {
  accountId: string | null;
  /** The account's name; null when the id is not in the list (or there is none). */
  name: string | null;
  /** False when `accountId` names no listed account: the page discloses this beside the id. */
  resolved: boolean;
}

/** Joins an account id to the loaded list by canonical id or former id (display only). */
export function resolveAccount(
  accountId: string | null,
  items: readonly CrmAccountListItem[]
): AccountRef {
  if (accountId === null) return { accountId, name: null, resolved: true };
  const hit = items.find((i) => i.account.id === accountId || i.account.aliases.includes(accountId));
  return hit
    ? { accountId, name: hit.account.name, resolved: true }
    : { accountId, name: null, resolved: false };
}
